import { useState } from "react";
import { parseApiError } from "../lib/http/apiErrors";
import { verifyPhoneChange, type PhoneVerificationChallenge } from "../features/auth/phoneVerification";
import type { AuthUser } from "../features/auth/types";

/**
 * Inline OTP entry rendered when a phone/WhatsApp number save comes back
 * HTTP 428 (PHONE_VERIFICATION_REQUIRED). No code is actually sent from this
 * app — the admin phone-OTP toggle collapses every WhatsApp OTP onto the
 * fixed code below, so this only confirms the save without messaging the
 * number. Do not wire up a "resend" action here; that would trigger a real
 * WhatsApp send via the backend.
 */
const BYPASS_OTP_HINT = "0000";

export function PhoneOtpConfirmPrompt({
  challenge,
  onVerified,
  onCancel,
}: {
  challenge: PhoneVerificationChallenge;
  onVerified: (user: AuthUser) => void;
  onCancel: () => void;
}) {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!otp.trim()) {
      setError("Enter the confirmation code.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      onVerified(await verifyPhoneChange(challenge.field, otp.trim()));
    } catch (err) {
      setError(parseApiError(err, "Invalid or expired code.").message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 p-3.5">
      <p className="text-sm font-medium text-blue-900">{challenge.message}</p>
      <p className="mt-0.5 text-xs text-blue-700">
        No WhatsApp message is sent from this app — enter{" "}
        <span className="font-mono font-semibold">{BYPASS_OTP_HINT}</span> to confirm.
      </p>

      {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder={BYPASS_OTP_HINT}
          autoFocus
          className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-center text-sm tracking-widest focus:border-blue-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !otp.trim()}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Confirming…" : "Confirm"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
